/**
 * An optimization section in the Flow Optimizer's window (user, 12/10/2026:
 * "rectangle pop up … see live on the right … switch between formulas /
 * rules / config"). Two parts: `OptimizerDialog` is the window — always
 * mounted by its step, so it grows from its middle on open and shrinks back
 * on close (user: "getting bigger and smaller") — and
 * `OptimizerBody` is what it holds, mounted only while open (its reads run
 * only then): the header, the View row (the Flow Optimizer's row label, tab
 * triggers and Live preview button), the open tab on the left (fading in on
 * a switch) and the live result on the right in its own framed card, the
 * footer. Edits go into the container's draft.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode, useState } from 'react';
import { LayoutGrid, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/layout/Button';
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/layout/Drawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/layout/Tabs';
import { cn } from '@/lib/utils';
import { ToolbarRowLabel } from '@/features/flow-optimizer/components';
import { PreviewPinButton, PreviewSplitLayout } from '@/features/flow-optimizer/components/preview/panel';

import { useEditorSave } from '../shell/editor-save-context';

/** The Flow Optimizer's tab trigger look. */
const TRIGGER = 'h-7 gap-1 px-2.5 py-0 text-[11px] data-[state=active]:bg-background data-[state=active]:shadow-sm';

export interface OptimizerTab {
  value: string;
  label: string;
  icon: LucideIcon;
  content: ReactNode;
}

/**
 * Centred by margins, not by a translate: the shared centre dialog's zoom
 * animation replaces its -50 % translate while it runs, so the window flew in
 * from the bottom-right corner. With the fixed size and `inset-0 m-auto` the
 * window's own keyframes only scale it, from and back to its middle.
 */
const WINDOW = cn(
  'inset-0 m-auto translate-x-0 translate-y-0 h-[min(94vh,1400px)] max-h-[min(94vh,1400px)]',
  // `!`: the shared dialog's tailwindcss-animate `animate-in/out` (unknown to tailwind-merge) would win otherwise.
  'data-[state=open]:!animate-dg-window-in data-[state=closed]:!animate-dg-window-out motion-reduce:!animate-none',
);

/** The window: open and close animate; its body exists only while open. */
export const OptimizerDialog: FC<{ open: boolean; onOpenChange: (open: boolean) => void; children: ReactNode }> = ({ open, onOpenChange, children }) => (
  <Drawer open={open} onOpenChange={onOpenChange}>
    <DrawerContent variant="center" width="w-[min(1600px,calc(100vw-2rem))]" className={WINDOW}>
      {children}
    </DrawerContent>
  </Drawer>
);

interface BodyProps {
  title: string;
  /** Beside the title — the container chip and what it is. */
  subtitle: ReactNode;
  /** Right of the header — the section's on/off and where its settings come from. */
  headerAside?: ReactNode;
  tabs: OptimizerTab[];
  /** The tab open first (default the first). */
  defaultTab?: string;
  /** The live result, on the right. */
  panel: ReactNode;
  panelWidthClass?: string;
  /**
   * The result fills the panel and scrolls itself (a long list keeps its
   * head in view and uses every pixel down to the bottom — user, 12/10/2026:
   * "lets show the most text on the screen"); otherwise the panel scrolls.
   */
  panelFill?: boolean;
  onClose: () => void;
}

const LivePanel: FC<{ fill: boolean; children: ReactNode }> = ({ fill, children }) => (
  <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-border/70 bg-card shadow-sm">
    <div className="flex shrink-0 items-center gap-2 border-b border-border/60 bg-muted/30 px-4 py-2 text-xs">
      <span className="relative flex h-2 w-2" aria-hidden>
        <span className="absolute inline-flex h-full w-full rounded-full bg-success opacity-60 motion-safe:animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-success" />
      </span>
      <span className="font-semibold">Live</span>
      <span className="text-muted-foreground">— updates as you edit</span>
    </div>
    <div className={cn('flex min-h-0 flex-1 flex-col overflow-x-hidden', fill ? 'overflow-y-hidden' : 'overflow-y-auto p-4 [scrollbar-gutter:stable]')}>{children}</div>
  </div>
);

export const OptimizerBody: FC<BodyProps> = ({ title, subtitle, headerAside, tabs, defaultTab, panel, panelWidthClass = 'lg:w-[44rem]', panelFill = false, onClose }) => {
  const [tab, setTab] = useState(defaultTab ?? tabs[0]?.value ?? '');
  const [pinned, setPinned] = useState(true);
  // User, 13/10/2026: save from inside the window, without leaving it.
  const saveCtx = useEditorSave();
  return (
    <>
      <DrawerHeader className="gap-0 px-5 pb-2 pt-3">
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
            <DrawerTitle className="shrink-0 text-sm font-semibold leading-tight">{title}</DrawerTitle>
            <span className="mx-0.5 h-3 w-px shrink-0 self-center bg-border/90" aria-hidden />
            {subtitle}
          </div>
          <div className="flex shrink-0 items-center gap-3">{headerAside}</div>
        </div>
        <DrawerDescription className="sr-only">{`${title}: ${tabs.map((t) => t.label).join(', ')} — the result updates live on the right.`}</DrawerDescription>
      </DrawerHeader>
      <DrawerBody className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-5 py-0 pb-4 pt-1">
        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex h-9 shrink-0 items-center gap-2 rounded-lg border border-border/60 bg-muted/15 px-2">
            <ToolbarRowLabel icon={LayoutGrid}>View</ToolbarRowLabel>
            <TabsList aria-label={`${title} views`} className="h-7 min-w-0 flex-1 justify-start gap-0.5 bg-transparent p-0">
              {tabs.map((t) => (
                <TabsTrigger key={t.value} value={t.value} className={TRIGGER}>
                  <t.icon className="h-3 w-3 shrink-0" aria-hidden />
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <div className="flex shrink-0 items-center border-l border-border/50 pl-2">
              <PreviewPinButton pinned={pinned} onToggle={() => setPinned((v) => !v)} />
            </div>
          </div>
          <PreviewSplitLayout active={pinned} panelWidthClass={cn(panelWidthClass, 'lg:border-l-0 lg:pl-4')} panel={<LivePanel fill={panelFill}>{panel}</LivePanel>}>
            {tabs.map((t) => (
              <TabsContent key={t.value} value={t.value} className="mt-0 pr-1 duration-200 animate-in fade-in-0 slide-in-from-bottom-1 focus-visible:outline-none motion-reduce:animate-none">
                {t.content}
              </TabsContent>
            ))}
          </PreviewSplitLayout>
        </Tabs>
      </DrawerBody>
      <DrawerFooter className="block w-full px-5 py-3">
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {saveCtx ? 'Save keeps this window open · Done only closes it (unsaved edits stay in the container).' : 'Your changes go into the container — Save container saves them.'}
          </span>
          <span className="flex items-center gap-2">
            {saveCtx ? (
              <Button type="button" size="sm" disabled={!saveCtx.dirty || saveCtx.saving || saveCtx.invalidReason !== null} title={saveCtx.invalidReason ?? undefined} onClick={saveCtx.save}>
                {saveCtx.saving ? 'Saving…' : saveCtx.label}
              </Button>
            ) : null}
            <Button type="button" size="sm" variant={saveCtx ? 'outline' : 'default'} onClick={onClose}>
              Done
            </Button>
          </span>
        </div>
      </DrawerFooter>
    </>
  );
};
